// Building blocks for the design-only screens (D-044). Each screen uses the live page frame
// (CommercePage, D-038 header, D-042 sizes) and says plainly at the top that it is a preview:
// actions are blocked with "Not live yet", tables show their columns and an empty state, and
// no example row is presented as store data.
import { useState, type ReactNode } from "react";
import { t } from "@ady/i18n";
import { Banner, Button, cx, Icon, Switch, type Availability, type IconName } from "@ady/ui";
import { CommercePage } from "../orders/ui";
import type { NavItem } from "../shell/nav";

export const NOT_LIVE: Availability = { kind: "pending-activation", reason: t("designs.notLive") };

export function DesignPage({
  item,
  lead,
  action,
  actionIcon = "plus",
  extraActions,
  wide = true,
  children,
}: {
  item: NavItem;
  lead: string;
  action?: string;
  actionIcon?: IconName;
  extraActions?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <CommercePage
      title={t(item.label)}
      lead={lead}
      anyOf={item.anyOf}
      testId="design-page"
      canonical={item.canonical}
      wide={wide}
      actions={
        action || extraActions ? (
          <>
            {extraActions}
            {action ? (
              <Button size="sm" icon={actionIcon} availability={NOT_LIVE}>
                {action}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
    >
      <Banner kind="info" title={t("designs.previewTitle")}>
        {t("designs.previewBody")}
      </Banner>
      {children}
    </CommercePage>
  );
}

/** View pills, as on the order list. Only the selection changes; the list stays empty. */
export function Pills({ labels }: { labels: string[] }) {
  const [on, setOn] = useState(0);
  return (
    <div className="ady-scroll-quiet flex gap-2 overflow-x-auto">
      {[t("designs.all"), ...labels].map((label, i) => (
        <button
          key={label}
          type="button"
          aria-pressed={on === i}
          onClick={() => setOn(i)}
          className={cx(
            "flex min-h-8 flex-none items-center rounded-full border px-3.5 text-xs font-medium whitespace-nowrap",
            on === i
              ? "border-transparent ady-bg-brand text-on-primary"
              : "border-card bg-surface text-fg hover:bg-hover",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** The empty state body: brand-tinted icon tile, title and one paragraph. */
export function Empty({ icon, title, body }: { icon: IconName; title: string; body: string }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      <span className="ady-bg-brand-tint mb-1 flex size-12 items-center justify-center rounded-panel">
        <Icon name={icon} size={24} gradient />
      </span>
      <p className="text-md font-bold">{title}</p>
      <p className="max-w-md text-sm text-fg-secondary">{body}</p>
    </div>
  );
}

/** A table with its column headers and an empty state where the rows will be. */
export function EmptyTable({
  caption,
  columns,
  icon,
  title,
  body,
}: {
  caption: string;
  columns: string[];
  icon: IconName;
  title: string;
  body: string;
}) {
  return (
    <div className="overflow-x-auto rounded-xs border border-subtle bg-surface">
      <table className="w-full border-collapse text-start">
        <caption className="ady-sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-subtle text-2xs font-bold text-fg-tertiary uppercase">
            {columns.map((c) => (
              <th key={c} scope="col" className="px-2.5 py-2 text-start whitespace-nowrap">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={columns.length}>
              <Empty icon={icon} title={title} body={body} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** A setting with its explanation and a blocked control on the right. */
export function PreviewRow({
  icon,
  title,
  help,
  control,
}: {
  icon: IconName;
  title: string;
  help: string;
  /** Defaults to a switch that is off and not live. */
  control?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xs border border-card bg-surface p-4 md:flex-row md:items-center">
      <span className="flex size-9 flex-none items-center justify-center rounded-xs bg-sunken text-fg-secondary">
        <Icon name={icon} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold">{title}</span>
        <span className="text-xs text-fg-tertiary">{help}</span>
      </div>
      <div className="flex-none">
        {control ?? (
          <Switch
            label={<span className="ady-sr-only">{title}</span>}
            checked={false}
            onCheckedChange={() => {}}
            availability={NOT_LIVE}
          />
        )}
      </div>
    </div>
  );
}

/** A card of options (rules, apps, topics): icon or mark, name, one line, a blocked action. */
export function OptionCard({
  icon,
  mark,
  title,
  body,
  action,
}: {
  icon?: IconName;
  mark?: ReactNode;
  title: string;
  body: string;
  action?: string;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xs border border-card bg-surface p-4">
      <div className="flex items-start gap-3">
        <span className="ady-bg-brand-tint flex size-10 flex-none items-center justify-center rounded-xs">
          {mark ?? (icon ? <Icon name={icon} size={20} gradient /> : null)}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-semibold">{title}</span>
          <span className="text-xs text-fg-secondary">{body}</span>
        </div>
      </div>
      {action ? (
        <Button size="sm" variant="secondary" availability={NOT_LIVE} className="self-start">
          {action}
        </Button>
      ) : null}
    </div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-md font-bold">{children}</h2>;
}
